import { type CollectionSummaryJSONObject, isCollectionVariant } from '@ikegps/core-api';
import type { CollectionModel } from '@ikegps/core-shared';
import { get } from 'lodash';
import { action, computed, makeObservable } from 'mobx';
import { ImageButton } from '../../components/button/image.button';
import type { PanelCollapseState } from '../../components/collapsible';
import { MaterialIcon } from '../../components/icon/icons';
import { IMithrilStateComponent } from '../../components/mithril.component';
import { ResizeHandle } from '../../components/resizable';
import { addStyles } from '../../components/theme';
import { T } from '../../i18n';
import type { MithrilVNode } from '../../mithril';
import { m } from '../../mithril';
import { IkePageName } from '../../page/page';
import type { TagState } from '../../page/tag.state';
import { Services } from '../../services';
import { CurrentState } from '../../state/page.state';
import { TagSortState } from '../../state/tag.sort.state';
import { VariantLayerState } from '../../state/variant.layer.state';
import { AppStore } from '../../store';
import { scrollSelectedIntoListView } from '../../util/scroll-into-list-view';
import { CaptureBar } from '../capture.bar/capture.bar';
import { CSS as CollectionCardCSS } from '../collection.list/collection.card.style';
import { CollectionList } from '../collection.list/collection.list';
import { CSS as CollectionListCSS } from '../collection.list/collection.list.style';
import { CollectionToolbar } from '../collection.toolbar/collection.toolbar';
import { VariantLayerSelector } from '../variant.layer.selector/variant.layer.selector';
import { GlobalLineDrawPopup } from '../line.draw.popup';
import { TagMap } from '../tag.map/tag.map';
import {
	COLLECTIONS_MIN_HEIGHT,
	CSS,
	INSET_MAP_MIN_HEIGHT,
	INSET_MAP_PANEL_VAR,
	Style,
} from './photomeasure.collection.list.style';
import { PhotomeasureMapOverlay } from './photomeasure.map.overlay';

addStyles(Style);

export class PhotomeasureCollectionList extends IMithrilStateComponent {
	private state: TagState;
	private toolbar: CollectionToolbar;
	private layerSelector = new VariantLayerSelector(() => CurrentState.inspectedTagId);
	private map: TagMap;

	private insetMapHandle = new ResizeHandle({
		axis: 'y',
		cssVar: INSET_MAP_PANEL_VAR,
		target: 'next',
		min: INSET_MAP_MIN_HEIGHT,
		minAdjacent: COLLECTIONS_MIN_HEIGHT,
		storageKey: 'panel.size/measure/inset',
		onResizeEnd: () => Services.Map.notifyResize(),
	});

	overlay: PhotomeasureMapOverlay;

	@computed get inspectedCapture() {
		const { inspectedCaptureId, inspectedCollectionRevision } = CurrentState;

		if (inspectedCaptureId && inspectedCollectionRevision) {
			return AppStore.Collection.get(inspectedCollectionRevision)?.state.getCapture(inspectedCaptureId);
		}

		return null;
	}

	constructor(state: TagState, collapse?: PanelCollapseState) {
		super();
		makeObservable(this);

		this.state = state;
		this.toolbar = new CollectionToolbar(this.state, {
			mapZoomOut: false,
			title: T.COLLECTION_TITLE,
			collapse,
		});
		this.map = new TagMap(this.state, 'photomeasureMap');

		this.overlay = new PhotomeasureMapOverlay({
			capture: this.inspectedCapture,
		});

		this.reaction(() => this.collection, this.ensureCollectionSelected);
		this.reaction(
			() => this.inspectedCapture,
			() => {
				const current = get(this.overlay, ['capture', 'objectId'], null);
				const next = get(this, ['inspectedCapture', 'objectId'], null);

				if (next !== current) {
					this.overlay.setCapture(this.inspectedCapture);
				}
			}
		);
		this.reaction(
			() => CurrentState.inspectedCollectionRevision,
			(revision) => {
				if (revision != null) {
					scrollSelectedIntoListView(CollectionListCSS.container, CollectionCardCSS.isInspected);
				}
			}
		);
	}

	/** Taken from the toolbar so the rail and the toolbar can't show different names. */
	get panelTitle(): string {
		return this.toolbar.title;
	}

	get nodes(): MithrilVNode {
		const collectionSummaries = this.visibleCollections;

		if (collectionSummaries == null) {
			return m('div', { className: CSS.container }, [
				m(
					'div',
					{ className: `${CSS.collections.content} ${CSS.collections.isEmpty}` },
					CollectionList.viewLoading(CurrentState.inspectedTagId)
				),
				CaptureBar.view(),
			]);
		}

		const tag = AppStore.Tag.get(CurrentState.inspectedTagId);
		const inspectedCollection = this.collection;

		return m('div', { className: CSS.container }, [
			m('div', { className: CSS.collections.container }, [
				m('div', { className: CSS.toolbar.container }, this.toolbar.view()),
				m('div', { className: CSS.collections.content }, [
					// The variant layer selector rides at the top of the collection list itself.
					CollectionList.view(this.state, tag, collectionSummaries, this.layerSelector.view()),
					CaptureBar.view(inspectedCollection),
				]),
			]),
			this.insetMapHandle.view(),
			// `map.view()` is a Mithril fragment carrying TagMap's onRemove (which tears down the shared-map
			// overlay). It MUST stay the sole child of `map.inner`: a fragment placed beside a
			// conditionally-null sibling (the popup) makes Mithril 1.x skip onRemove, leaking the overlay
			// onto the shared map after navigation. Keep the popup a sibling of the DIV, not the fragment.
			m('div', { className: CSS.map.container }, [
				m('div', { className: CSS.map.inner }, this.map.view()),
				GlobalLineDrawPopup.view(),
			]),
			this.viewMapButton(inspectedCollection),
		]);
	}

	@computed private get visibleCollections(): CollectionSummaryJSONObject[] {
		if (CurrentState.inspectedTagId == null || AppStore.Tag.get(CurrentState.inspectedTagId) == null) {
			return null;
		}

		// Show the selected variant layer's collections (base poles when there are no layers).
		const revisions = VariantLayerState.effectiveRevisions(CurrentState.inspectedTagId);

		const collectionSummaries: CollectionSummaryJSONObject[] = [];
		for (const revision of revisions) {
			const collectionSummary = AppStore.CollectionSummary.get(revision);
			if (collectionSummary != null) {
				collectionSummaries.push(collectionSummary);
			}
		}

		TagSortState.sortSummaries(collectionSummaries);
		CurrentState.Tag.resetLastSelectedCollectionIndex();
		CurrentState.Tag.setExpandedCollectionsUUIDs(collectionSummaries.map((_) => _.uuid));
		return collectionSummaries;
	}

	@action.bound private ensureCollectionSelected() {
		const tagId = CurrentState.inspectedTagId;
		const tagSummary = AppStore.TagSummary.get(tagId);

		CurrentState.setInspectedTagId(tagId);

		if (tagSummary == null) {
			return;
		}

		const revision = CurrentState.inspectedCollectionRevision;
		// Variants are intentionally excluded from tagSummary.revisions (they aren't top-level job-list
		// entries) but are still valid inspections — don't bounce the selection off them.
		if (revision != null && this.isInspectedVariant(revision)) {
			return;
		}

		const revisions = VariantLayerState.effectiveRevisions(tagId);
		if (revisions.indexOf(revision) === -1) {
			CurrentState.setInspectedCollection(revisions[0]);
		}
	}

	private isInspectedVariant(revision: string): boolean {
		const summary = AppStore.CollectionSummary.get(revision);
		return summary != null && isCollectionVariant(summary);
	}

	private viewMapButton(collection: CollectionModel): MithrilVNode {
		const hrefParams = collection != null ? { t: collection.jobId, collection: collection.uuid } : undefined;
		return ImageButton.view(MaterialIcon.MAP, {
			className: CSS.map.button,
			title: T.NAVIGATE_MAP,
			type: 'a',
			href: IkePageName.map,
			hrefParams,
			onclick: (e) => {
				// eslint-disable-next-line @typescript-eslint/no-floating-promises
				Services.Window.confirmPageChange(e, Services.Route.getRouteURL(IkePageName.map, hrefParams));
			},
		});
	}

	@computed private get collection(): CollectionModel | null {
		if (CurrentState.CollectionEdit.isEditing) {
			return CurrentState.CollectionEdit.collection;
		}

		return AppStore.Collection.get(CurrentState.inspectedCollectionRevision);
	}
}
